import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      value: "708-856-1055",
      link: "tel:708-856-1055",
      description: "Call us Monday-Saturday"
    },
    {
      icon: Mail,
      title: "Email",
      value: "ejquintana875@gmail.com",
      link: "mailto:ejquintana875@gmail.com",
      description: "We respond within 24 hours"
    },
    {
      icon: MapPin,
      title: "Location",
      value: "Lagrange, IL",
      link: "https://www.google.com/maps/search/Lagrange+IL",
      description: "Serving the Western Suburbs"
    },
    {
      icon: Clock,
      title: "Hours",
      value: "Mon-Fri: 8AM-6PM",
      description: "Sat: 9AM-3PM"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-blue-400 font-bold tracking-wider text-sm">GET IN TOUCH</span>
          <h1 className="text-4xl md:text-6xl font-black text-white mt-4 mb-6 font-['Chivo']">
            Contact Us
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Have questions about our services? Ready to start your project? 
            We're here to help!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8 font-['Chivo']">
                Let's Talk About Your Project
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you're planning a small update or a major renovation, 
                we're here to answer your questions and provide expert guidance.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                {contactInfo.map((item, idx) => (
                  <Card key={idx} className="border-slate-200">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900">{item.title}</h3>
                          {item.link ? (
                            <a 
                              href={item.link}
                              className="text-blue-600 hover:text-blue-700 font-medium"
                              target={item.link.startsWith('http') ? "_blank" : undefined}
                              rel={item.link.startsWith('http') ? "noopener noreferrer" : undefined}
                              data-testid={`contact-${item.title.toLowerCase()}`}
                            >
                              {item.value}
                            </a>
                          ) : (
                            <p className="text-slate-900 font-medium">{item.value}</p>
                          )}
                          <p className="text-slate-500 text-sm">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Map */}
              <div className="map-container aspect-video rounded-sm overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47609.82667261967!2d-87.90000000000001!3d41.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e479827e8f42f%3A0x56c98e6f6c0ec4a2!2sLa%20Grange%2C%20IL!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="875 Corp Location - Lagrange, IL"
                  data-testid="contact-map"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="text-2xl font-['Chivo']">Send Us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="h-12"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(708) 555-0123"
                        className="h-12"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="h-12"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or ask us a question..."
                      required
                      rows={6}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-sm btn-primary"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 spinner" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 font-['Chivo']">
            Service Areas
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            We proudly serve Lagrange and the surrounding communities in the western suburbs of Chicago.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              "Lagrange",
              "Lagrange Park",
              "Western Springs",
              "Brookfield",
              "Riverside",
              "Hinsdale",
              "Lyons",
              "Berwyn",
              "Countryside",
              "Indian Head Park"
            ].map((area, idx) => (
              <span 
                key={idx}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-sm font-medium"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
